import { Notes } from './AuthModels';

export interface CourseSeason {
  courseid: string;
  courseid_seasonid: string;
  description: string;
  position: number;
  seasonid: string;
  title: string;
}

export interface CourseSeasonMovieUser {
  courseid_seasonid_movieid_userid: string;
  movieid: string;
  courseid: string;
  seasonid: string;
  userid: string;
  videostatus: string;
  videowatched: string;
}

export interface Quiz {
  answer: string;
  courseid: string;
  courseid_seasonid_movieid_quizid: string;
  movieid: string;
  options: string;
  quizid: string;
  seasonid: string;
  title: string;
  videoposition: string;
}

export interface CourseSeasonMovie {
  courseid: string;
  courseid_seasonid_movieid: string;
  courseseasonmovieuser: CourseSeasonMovieUser;
  attachments?: CourseAttachment[];
  chaturl: string;
  description: string;
  exercises: [];
  exerciseshortmessage: string;
  filter: string;
  scheduledrelease: string;
  movieid: string;
  notes: Array<Notes>;
  position: number;
  quizzes: Array<Quiz>;
  seasonid: string;
  thumb: string;
  thumburlvertical?: string;
  title: string;
  url: string;
  urlvertical?: string;
  urlfiles?: string;
  videoduration: string;
  videotype: string;
}

export interface Course {
  comingsoon: string;
  courseid: string;
  title: string;
  clicksign_is_signed: string;
  status_payment: string;
  subscription: boolean;
  subscription_installments: number;
  description: string;
  thumburl: string;
  thumburlvertical?: string;
  teaserurl: string;
  levelidlist: string;
  priority: number;
  hasCheckout: boolean;
  pix_discount: string;
  pix_price: number;
  userprogress: number;
  certificateUserNumber: number;
  max_installments: number;
  schoolid: string;
  prerequirement: string;
  prerequirementtitle: string;
  certificatebackground: string;
  totalhours: number;
  currenttoprankingposition: number;
  is_owner: boolean;
  certificate: boolean;
  is_live: boolean;
  blocked: boolean;
  price: number;
}

export interface Exercise {
  answer: string;
  contenttype: string;
  courseid: string;
  courseid_seasonid_movieid_exerciseid: string;
  exerciseid: string;
  movieid: string;
  options: string;
  position: number;
  question: string;
  questiontype: string;
  seasonid: string;
  url: string;
}

export interface CourseAttachment {
  attachmentid: string;
  name: string;
  url: string;
  filetype?: string;
  filesize?: number;
}

export interface AdminCourseOutput {
  pages: number;
  courses: AdminCourse[];
}

export interface AdminCourse {
  courseid: string;
  title: string;
  schoolid: string;
  description: string;
  thumburl: string;
  teaserurl: string;
  thumburlvertical: string;
  levelidlist: string; //'["Ministério"]' ;
  comingsoon: string;
  profileid: string;
  price: number;
  is_owner: boolean;
  certificate: boolean;
  priority: number;
  currenttoprankingposition: number;
  userprogress: number;
    certificateUserNumber: number;
  // Hoje a Lambda de backoffice/course/all ainda não devolve este campo.
  status?: string;
}

/**
 * Curso completo, como gravado em NL_Course (GET /backoffice/course). A
 * listagem (`AdminCourse`) passa por um modelo Java que descarta seção e
 * pagamento; a edição precisa do item inteiro.
 */
export interface AdminCourseDetail extends Omit<AdminCourse, 'price' | 'priority'> {
  price: number;
  priority?: number;
  sectionid: string; // lista JSON: '["SECAO"]'
  name?: string;
  max_installments?: number;
  pix_price?: number;
  pix_discount?: string; // "8%"
  subscription?: boolean;
  subscription_installments?: number;
}

/** Campos aceitos pelo POST /backoffice/course. `null` remove o atributo. */
export interface CourseChanges {
  title?: string;
  description?: string;
  sectionid?: string;
  status?: 'Active' | 'Inactive';
  comingsoon?: 'true' | 'false';
  priority?: number;
  price?: number;
  max_installments?: number | null;
  pix_price?: number | null;
  pix_discount?: string | null;
  subscription?: boolean | null;
  subscription_installments?: number | null;
}

export interface CourseImages {
  thumburl_base64?: string;
  thumburlvertical_base64?: string;
  teaserurl_base64?: string;
}

export const isCourseActive = (course: AdminCourse): boolean =>
  course.status !== 'Inactive';

/**
 * Status "legível" de um curso, derivado dos dois campos que o backend
 * realmente devolve: `status` (`Active`/`Inactive`) e a flag `comingsoon`.
 * Os três estados são mutuamente exclusivos:
 *   - `inactive` — `status === 'Inactive'` (some da home do aluno)
 *   - `soon`     — `comingsoon === 'true'` (exibe "Em breve")
 *   - `active`   — qualquer outro caso
 */
export type CourseStatus = 'active' | 'soon' | 'inactive';

export const getCourseStatus = (course: AdminCourse): CourseStatus => {
  if (course.status === 'Inactive') return 'inactive';
  if (course.comingsoon === 'true') return 'soon';
  return 'active';
};
